import { useQuery } from '@tanstack/react-query';
import { getConfig } from '@leagueplatform/config';
import { leagueFetch } from '@leagueplatform/league-fetch';
import { useEffect } from 'react';

export const getContentUrlConfigValue = () =>
  getConfig().config.core.contentUrl;

/**
 * @deprecated Use the hook "useContentBlobUrl" instead
 */
export const getContentUrl = (contentId: string) => {
  console.warn(
    'Warning: "getContentUrl" is deprecated. Use the hook "useContentBlobUrl" instead.',
  );
  const contentPath = `content/${contentId}`;
  return contentId ? `${getContentUrlConfigValue()}/${contentPath}` : '';
};

const CONTENT_SERVER_QUERY_KEY = 'CONTENT_SERVER_QUERY_KEY';
const emptyBlob = new Blob([]);
const emptyBlobURL = URL.createObjectURL(emptyBlob);

/**
 * Hook to make an authenticated fetch to the content server for an asset.
 * Since hooks cannot be called conditionally, the function overloads
 * provide type support for different contentId types.
 *
 * @param {contentId} ID of the asset on the content server, either a
 *   string or else falsy (for cases where a placeholder is used)
 * @returns undefined if a falsy contentId is provided, otherwise returns a
 *   blob URL which can be used anywhere an asset URL is rendered
 */

// Function overload signatures
export function useContentBlobUrl(contentId: string): string;
export function useContentBlobUrl(contentId: undefined | null): undefined;
export function useContentBlobUrl(
  contentId: string | undefined | null,
): string | undefined;

// Function implementation
export function useContentBlobUrl(contentId: string | undefined | null) {
  const { data: contentBlobURL } = useQuery<string, Error>(
    [CONTENT_SERVER_QUERY_KEY, contentId],
    async () => {
      const response = await leagueFetch(`content/${contentId}`, {
        method: 'GET',
        api: 'content',
      });

      // extract the response body as a Blob (raw data) object
      const blob = await response.blob();

      // create a browser URL to access this blob object
      return URL.createObjectURL(blob);
    },
    { enabled: Boolean(contentId) },
  );

  useEffect(
    () =>
      // clean up
      () => {
        if (contentBlobURL) {
          URL.revokeObjectURL(contentBlobURL);
        }
      },
    [contentBlobURL],
  );

  if (!contentId) {
    return undefined;
  }

  return contentBlobURL || emptyBlobURL;
}
